import React, { useEffect, useState } from 'react';
import { elementType, arrayOf, shape, string, func, oneOf, bool } from 'prop-types';
import { Ellipsis } from '@nike/nike-design-system-icons';
import { Box, Flex } from 'rebass';
import styled from '@emotion/styled';
import { nike } from '@nike/nike-design-tokens';
import { Link } from 'react-router-dom';

const { podiumCdsColorGrey200, podiumCdsColorTextPrimaryInverse, podiumCdsColorBorderSecondary } = nike;

const Menu = styled.div`
  background: ${podiumCdsColorTextPrimaryInverse};
  border-radius: 4px;
  box-shadow: 0 0 0 1px ${podiumCdsColorBorderSecondary};
  overflow: auto;
  position: absolute;
  right: ${({ align }) => (align === 'right' ? 0 : 'auto')};
  top: 30px;
  width: 200px;
  & .disabled {
    background: rgb(245, 245, 245);
    color: rgb(204, 204, 204);
    border-color: transparent;
    pointer-events: none;
  }
`;

const MenuOption = styled(Link)`
  display: flex;
  align-items: center;
  padding-left: 15px;
  padding-right: 15px;
  height: 60px;
  text-align: center;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: ${podiumCdsColorGrey200};
  }
  &:nth-of-type(odd) {
    border-top: 1px solid ${podiumCdsColorGrey200};
    border-bottom: 1px solid ${podiumCdsColorGrey200};
  }
`;

/**
 * @name IconMenu
 * @description allow for varied components to be rendered within a dropdown
 * @param { arrayOf } menuOptions to be rendered within container
 * @param {string} align oneOf 'left' or 'right'. Aligns the dropdown to the left or right of the icon
 * @param { elementType } The Nike Design System Icon to be rendered
 * @example
 * <IconMenu menuOptions={[{text: 'Account Management', to: '/admin/accounts'}]} />
 */

const IconMenu = ({ menuOptions, align, Icon, openOnHover }) => {
  const [open, setOpen] = useState(false);
  const handleClick = event => {
    if (!event.target.closest('#iconMenu')) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <Box id='iconMenu' sx={{ position: 'relative', width: '25px', zIndex: '1000', fontSize: '14px' }}>
      <Flex
        data-testid='ellipsisIcon'
        onClick={() => setOpen(true)}
        onMouseEnter={openOnHover ? () => setOpen(true) : () => {}}
        style={{ cursor: 'pointer' }}
      >
        <Icon alt='Icon Menu' />
      </Flex>
      {open && (
        <Menu align={align}>
          {menuOptions.map(option => (
            <div key={option.text} className={option.disabled ? 'disabled' : ''}>
              <MenuOption
                to={option.to || '#'}
                disabled={option.disabled}
                onClick={
                  option.disabled
                    ? () => {}
                    : () => {
                        option.onClick?.();
                        setOpen(false);
                      }
                }
              >
                {option.text}
              </MenuOption>
            </div>
          ))}
        </Menu>
      )}
    </Box>
  );
};

IconMenu.defaultProps = {
  align: 'left',
  Icon: Ellipsis,
  menuOptions: [],
  openOnHover: false
};

IconMenu.propTypes = {
  align: oneOf(['left', 'right']),
  Icon: elementType,
  menuOptions: arrayOf(shape({ text: string.isRequired, to: string, onClick: func })),
  openOnHover: bool
};

export default IconMenu;
